/**
 * Firebase Realtime Database Configuration for Football API Caching
 * Uses RTDB as a shared cache layer to reduce API calls and improve performance
 */

import { getDatabase } from 'firebase/database';
import { app } from '../../../../firebase';

// Initialize RTDB with the specific database URL
const RTDB_URL = 'https://just-rhythm-445002-s8-e92d0.firebaseio.com/';

// Get database instance
export const rtdb = getDatabase(app, RTDB_URL);

// Cache paths configuration
export const CACHE_PATHS = {
  FIXTURES: 'cache/fixtures',
  ODDS_PREMATCH: 'cache/odds/prematch',
  ODDS_LIVE: 'cache/odds/live',
  STANDINGS: 'cache/standings',
  STATIC: 'cache/static',
  METADATA: 'cache/metadata'
};

// TTL configurations (in milliseconds)
export const TTL_CONFIG = {
  LIVE_ODDS: 30 * 1000,        // 30 seconds for live odds
  LIVE_FIXTURES: 60 * 1000,    // 1 minute for live fixtures
  PREMATCH_ODDS: 5 * 60 * 1000, // 5 minutes for pre-match odds (slightly increased)
  FIXTURES: 60 * 60 * 1000,    // 60 minutes for regular fixtures (was 5 min - HUGE SAVINGS!)
  STANDINGS: 60 * 60 * 1000,   // 1 hour for standings
  STATIC: 24 * 60 * 60 * 1000  // 24 hours for static data (leagues, teams)
};

// Dynamic TTL based on game state and proximity
export const getDynamicTTL = (fixture, dataType = 'fixtures') => {
  if (!fixture) return TTL_CONFIG.FIXTURES;

  const now = Date.now();
  const kickoff = new Date(fixture.fixture?.date || fixture.date || fixture).getTime();
  const timeDiff = kickoff - now;
  const status = fixture.fixture?.status?.short || fixture.status?.short || '';

  // PRIORITY 1: Live games - ultra fresh
  if (status === 'LIVE' || status === '1H' || status === 'HT' || status === '2H' ||
      status === 'ET' || status === 'P' || status === 'BT') {
    return dataType === 'odds' ? 15 * 1000 : 30 * 1000; // 15s for odds, 30s for fixtures
  }

  // PRIORITY 2: Just finished (within 30 minutes)
  if ((status === 'FT' || status === 'AET' || status === 'PEN') &&
      timeDiff > -1800000) {
    return 2 * 60 * 1000; // 2 minutes for final score updates
  }

  // PRIORITY 3: Starting very soon (within 15 minutes)
  if (timeDiff > 0 && timeDiff < 900000) {
    return 1 * 60 * 1000; // 1 minute
  }

  // PRIORITY 4: Starting today (within 2 hours)
  if (timeDiff > 0 && timeDiff < 2 * 3600000) {
    return 5 * 60 * 1000; // 5 minutes
  }

  // PRIORITY 5: Today's games
  if (timeDiff > 0 && timeDiff < 24 * 3600000) {
    return 15 * 60 * 1000; // 15 minutes
  }

  // PRIORITY 6: Tomorrow's games
  if (timeDiff > 24 * 3600000 && timeDiff < 48 * 3600000) {
    return 30 * 60 * 1000; // 30 minutes
  }

  // PRIORITY 7: Future games or finished games
  return 60 * 60 * 1000; // 1 hour
};

// Feature flags for gradual rollout
export const RTDB_FEATURES = {
  USE_RTDB_CACHE: true,
  CACHE_FIXTURES: true,
  CACHE_ODDS: true,
  CACHE_STANDINGS: true,
  ENABLE_ANALYTICS: true,
  ENABLE_DEBUG: process.env.NODE_ENV === 'development',
  USE_DYNAMIC_TTL: true  // Enable dynamic TTL
};

// Helper to generate cache keys (Firebase-safe)
export const getCacheKey = (type, ...params) => {
  // Replace invalid Firebase key characters
  return params.join('_').replace(/[.#$\[\]]/g, '_');
};

// Helper to check if cache is expired
export const isCacheExpired = (cacheData) => {
  if (!cacheData || !cacheData.ttl) return true;
  return Date.now() > cacheData.ttl;
};

// Helper to create cache entry
export const createCacheEntry = (data, ttlMs) => ({
  data,
  ttl: Date.now() + ttlMs,
  lastUpdated: Date.now(),
  hits: 0
});

// Helper to get TTL based on data type
export const getTTLForDataType = (dataType, isLive = false) => {
  if (isLive) {
    return dataType === 'odds' ? TTL_CONFIG.LIVE_ODDS : TTL_CONFIG.LIVE_FIXTURES;
  }
  
  const ttlMap = {
    'fixtures': TTL_CONFIG.FIXTURES,
    'odds': TTL_CONFIG.PREMATCH_ODDS,
    'standings': TTL_CONFIG.STANDINGS,
    'leagues': TTL_CONFIG.STATIC,
    'teams': TTL_CONFIG.STATIC,
    'bookmakers': TTL_CONFIG.STATIC
  };
  
  return ttlMap[dataType] || TTL_CONFIG.FIXTURES;
};

// Debug logging
export const logCache = (action, path, details = {}) => {
  if (RTDB_FEATURES.ENABLE_DEBUG) {
    const emoji = {
      'hit': '✅',
      'miss': '❌',
      'set': '💾',
      'expired': '⏰',
      'error': '🚨',
      'info': '📊'
    }[action] || '📦';
    
    console.log(`${emoji} RTDB Cache ${action}:`, path, details);
  }
};

// Analytics helper with improved error handling
export const trackCacheMetric = async (metric, value = 1) => {
  if (!RTDB_FEATURES.ENABLE_ANALYTICS) return;

  try {
    // Check if rtdb is initialized
    if (!rtdb) {
      if (RTDB_FEATURES.ENABLE_DEBUG) {
        console.warn('RTDB not initialized, skipping metric tracking');
      }
      return;
    }

    const { ref, runTransaction } = await import('firebase/database');
    const metricRef = ref(rtdb, `${CACHE_PATHS.METADATA}/stats/${metric}`);

    await runTransaction(metricRef, (current) => {
      return (current || 0) + value;
    }, {
      // Add retry logic for better reliability
      applyLocally: false  // Don't apply locally first, wait for server
    });
  } catch (error) {
    // Only log errors in debug mode to avoid console pollution
    if (RTDB_FEATURES.ENABLE_DEBUG) {
      console.warn('Metric tracking failed (non-critical):', metric, error.message);
    }
    // Silent fail for analytics - it's not critical functionality
  }
};